import React, { useState } from "react";
import { Star, Quote, Building, Calendar, Award } from "lucide-react";
import { Testimonial } from "../sample.data";
import { getVisibleSortedItems, getFeaturedItems } from "../utils";

interface TestimonialsProps {
  testimonials: Testimonial[];
  showFeaturedOnly?: boolean;
}

export const TestimonialsSection: React.FC<TestimonialsProps> = ({
  testimonials,
  showFeaturedOnly = false,
}) => {
  const [hoveredTestimonial, setHoveredTestimonial] = useState<string | null>(
    null
  );

  const visibleTestimonials = showFeaturedOnly
    ? getFeaturedItems(testimonials)
    : getVisibleSortedItems(testimonials);

  if (visibleTestimonials.length === 0) return null;

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 transition-all duration-200 ${
          i < rating
            ? "text-amber-400 fill-current drop-shadow-sm"
            : "text-gray-300"
        }`}
      />
    ));
  };

  return (
    <div className="grid gap-8 md:grid-cols-2">
      {visibleTestimonials.map((testimonial) => {
        const isHovered = hoveredTestimonial === testimonial.id;

        return (
          <article
            key={testimonial.id}
            className="group relative overflow-hidden rounded-2xl bg-white/90 backdrop-blur-xl border border-white/20 hover:bg-white/95 transition-all duration-700 hover:shadow-2xl hover:shadow-amber-500/10"
            onMouseEnter={() => setHoveredTestimonial(testimonial.id)}
            onMouseLeave={() => setHoveredTestimonial(null)}
          >
            {/* Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50/50 via-transparent to-orange-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

            {/* Featured Badge */}
            {testimonial.featured && (
              <div className="absolute -top-2 -right-2 z-20 flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full text-xs font-medium shadow-lg">
                <Award className="h-3 w-3 fill-current" />
                Featured
              </div>
            )}

            {/* Quote Icon */}
            <div className="absolute top-6 right-6 z-10">
              <div
                className={`relative transition-all duration-500 ${
                  isHovered ? "scale-110 rotate-12" : "scale-100 rotate-0"
                }`}
              >
                <Quote
                  className="h-12 w-12 text-amber-200/60"
                  fill="currentColor"
                />
              </div>
            </div>

            <div className="relative z-20 p-8">
              {/* Rating */}
              {testimonial.rating && (
                <div className="flex items-center gap-1 mb-6">
                  <div className="flex items-center gap-0.5">
                    {renderStars(testimonial.rating)}
                  </div>
                  <span className="ml-2 text-sm font-medium text-gray-600">
                    {testimonial.rating}/5
                  </span>
                </div>
              )}

              {/* Testimonial Content */}
              <blockquote className="text-gray-700 leading-relaxed mb-6 text-lg italic relative">
                <span className="text-2xl text-amber-300 absolute -top-2 -left-2">
                  "
                </span>
                <div className="relative z-10 pl-4">{testimonial.content}</div>
                <span className="text-2xl text-amber-300 absolute -bottom-4 right-0">
                  "
                </span>
              </blockquote>

              {/* Author Information */}
              <footer className="flex items-start gap-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {testimonial.authorAvatar ? (
                    <div className="relative">
                      <img
                        src={testimonial.authorAvatar}
                        alt={testimonial.author}
                        className="h-14 w-14 rounded-2xl object-cover shadow-lg ring-3 ring-white"
                      />
                      <div className="absolute -bottom-1 -right-1 h-5 w-5 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full border-2 border-white" />
                    </div>
                  ) : (
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-lg">
                        {testimonial.author.charAt(0)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Author Details */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-gray-900 text-lg leading-tight">
                    {testimonial.author}
                  </h4>

                  {testimonial.authorTitle && (
                    <p className="text-blue-600 font-medium text-sm mt-0.5">
                      {testimonial.authorTitle}
                    </p>
                  )}

                  {testimonial.authorCompany && (
                    <div className="flex items-center gap-1.5 mt-2">
                      <Building className="h-3.5 w-3.5 text-gray-500" />
                      <span className="text-gray-600 text-sm font-medium">
                        {testimonial.authorCompany}
                      </span>
                    </div>
                  )}

                  {testimonial.date && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <Calendar className="h-3.5 w-3.5 text-gray-500" />
                      <span className="text-gray-500 text-sm">
                        {new Date(testimonial.date).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </footer>

              {/* Decorative Elements */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            </div>

            {/* Hover Glow Effect */}
            <div
              className={`absolute inset-0 rounded-2xl transition-all duration-700 pointer-events-none ${
                isHovered
                  ? "shadow-2xl shadow-amber-500/20 ring-1 ring-amber-200"
                  : "shadow-none"
              }`}
            />
          </article>
        );
      })}
    </div>
  );
};
